// Pure-function TypeScript port of `listblox/deals/calculations.py`.
//
// Both ports must produce identical outputs for every fixture case in
// `docs/dealcalc/deal_math_fixture.json`. JS Math.round is half-away-
// from-zero for non-negative inputs, which matches Python's
// `floor(x + 0.5)` helper there — meaning this file can call
// `Math.round` directly and stay bit-equal with the Python port.
//
// Read `docs/dealcalc/deal_calc_math_contract.md` before touching.

import {
	createBlankDealState,
	IDealLineItem,
	IDealHoldingItem,
	IDealState,
} from './defaults';

function num(v: any, def: number = 0): number {
	if (v === null || v === undefined) {
		return def;
	}
	const n = Number(v);
	if (!Number.isFinite(n)) {
		return def;
	}
	return n;
}

function mergeWithTemplate(deal: Partial<IDealState> | null | undefined): IDealState {
	const template = createBlankDealState();
	const stored = (deal && typeof deal === 'object') ? deal : {};
	return {
		...template,
		...stored,
		property: {
			...template.property,
			...((stored as any).property || {}),
		},
	};
}

export function distributeByWeight(total: number, items: ReadonlyArray<IDealLineItem>): Array<IDealLineItem> {
	const totalWeight = items.reduce((s, i) => s + num(i.weight), 0);
	if (totalWeight === 0) {
		return items.map(i => ({...i}));
	}
	return items.map(i => ({...i, amount: Math.round(total * (num(i.weight) / totalWeight))}));
}

export function sumLineItems(items: ReadonlyArray<{amount?: number; monthly?: number}>, key: 'amount' | 'monthly' = 'amount'): number {
	return items.reduce((s, i) => s + num((i as any)[key]), 0);
}

export function solveARVForTargetROI(cashInDeal: number, targetROIPct: number, sellingPct: number, isFlip: boolean): number {
	if (!cashInDeal || cashInDeal <= 0) {
		return 0;
	}
	const target = targetROIPct / 100;
	if (!isFlip || sellingPct >= 1) {
		return cashInDeal * (1 + target);
	}
	return Math.round((cashInDeal * (1 + target)) / (1 - sellingPct));
}

export function deriveMortgageMonthly(financedAmount: number, financedRatePct: number): number {
	const amt = num(financedAmount);
	const pct = num(financedRatePct);
	return Math.round((amt * pct / 100) / 12);
}

export function deriveBuildCostTotal(buildCostPerSqft: number, sqft: number): number {
	return Math.round(num(buildCostPerSqft) * num(sqft));
}

export function deriveBuildCostPerSqft(buildCostTotal: number, sqft: number): number {
	const sf = num(sqft);
	if (sf <= 0) {
		return 0;
	}
	return Math.round(num(buildCostTotal) / sf);
}

export interface IDealMetrics {
	listPrice: number;
	effectiveListPrice: number;
	reductionPct: number;
	purchasePrice: number;
	closingCostsComputed: Array<IDealLineItem & {computed: number}>;
	totalClosingCosts: number;
	totalAcquisition: number;
	enclosedAddedCost: number;
	enclosedAddedValue: number;
	renoSubtotal: number;
	contingencyAmount: number;
	totalRenoCost: number;
	totalMonthlyHolding: number;
	totalHoldingCosts: number;
	arvWithEnclosed: number;
	adjustedARV: number;
	sellingCostsComputed: Array<IDealLineItem & {computed: number}>;
	totalSellingCosts: number;
	totalCashInDeal: number;
	projectedProfit: number;
	projectedROI: number;
	maxAllowableOffer: number;
	passes70Rule: boolean;
	effectiveSellingPct: number;
	targetARV20: number;
	targetARVForIMO: number;
	profitMarginPct: number;
	ltcPct: number;
	ltvPct: number;
	isCashDeal: boolean;
}

export function computeDealMetrics(deal: Partial<IDealState> | null | undefined): IDealMetrics {
	const merged = mergeWithTemplate(deal);

	const listPrice = Math.max(0, num(merged.listPrice));
	const rawReduction = num(merged.priceReductionPct);
	const reductionPct = Math.min(30, Math.max(0, rawReduction));
	const purchasePrice = listPrice > 0
		? Math.round(listPrice * (1 - reductionPct / 100))
		: Math.max(0, num(merged.purchasePrice));
	const effectiveListPrice = listPrice > 0 ? listPrice : purchasePrice;

	const closingItems = merged.closingCosts || [];
	const closingCostsComputed = closingItems.map(c => ({
		...c,
		computed: num(c.weight) > 0 ? Math.round(purchasePrice * num(c.weight)) : num(c.amount),
	}));
	const totalClosingCosts = closingCostsComputed.reduce((s, c) => s + c.computed, 0);
	const totalAcquisition = purchasePrice + totalClosingCosts;

	const enclosedSqft = num(merged.enclosedSqft);
	const enclosedAddedCost = enclosedSqft * num(merged.enclosedCostPerSqft);
	const enclosedAddedValue = enclosedSqft * num(merged.enclosedValuePerSqft);
	const enclosedCostApplied = merged.enclosedCostInReno ? enclosedAddedCost : 0;
	const renoSubtotal = num(merged.renoTotal) + enclosedCostApplied;
	const contingencyAmount = renoSubtotal * (num(merged.contingencyPct) / 100);
	const totalRenoCost = renoSubtotal + contingencyAmount;

	const holdingItems: ReadonlyArray<IDealHoldingItem> = merged.holdingItems || [];
	const totalMonthlyHolding = sumLineItems(holdingItems, 'monthly');
	const totalHoldingCosts = totalMonthlyHolding * num(merged.holdMonths);

	const arvWithEnclosed = num(merged.baseARV) + enclosedAddedValue;
	const adjustedARV = arvWithEnclosed * (1 + num(merged.marketingMultiplier) / 100);

	const sellingItems = merged.sellingCosts || [];
	const sellingCostsComputed = sellingItems.map(c => ({
		...c,
		computed: num(c.weight) > 0 ? Math.round(adjustedARV * num(c.weight)) : num(c.amount),
	}));
	const isFlip = !!merged.isFlip;
	const totalSellingCosts = isFlip ? sellingCostsComputed.reduce((s, c) => s + c.computed, 0) : 0;

	const totalCashInDeal = totalAcquisition + totalRenoCost + totalHoldingCosts;
	const projectedProfit = isFlip
		? adjustedARV - totalCashInDeal - totalSellingCosts
		: adjustedARV - totalCashInDeal;
	const projectedROI = totalCashInDeal > 0 ? (projectedProfit / totalCashInDeal) * 100 : 0;

	const maxAllowableOffer = adjustedARV * 0.7 - totalRenoCost;
	const passes70Rule = purchasePrice <= maxAllowableOffer && purchasePrice > 0;

	const effectiveSellingPct = sellingItems.reduce((s, c) => s + num(c.weight), 0);
	const targetARV20 = solveARVForTargetROI(totalCashInDeal, 20, effectiveSellingPct, isFlip);
	const targetARVForIMO = purchasePrice > 0 ? (purchasePrice + totalRenoCost) / 0.7 : 0;

	const financedAmount = Math.max(0, num(merged.financedAmount));
	const isCashDeal = financedAmount === 0;
	const profitMarginPct = totalCashInDeal > 0
		? ((adjustedARV - totalCashInDeal) / totalCashInDeal) * 100
		: 0;
	const ltcPct = totalCashInDeal > 0 ? (financedAmount / totalCashInDeal) * 100 : 0;
	const ltvPct = adjustedARV > 0 ? (financedAmount / adjustedARV) * 100 : 0;

	return {
		listPrice,
		effectiveListPrice,
		reductionPct,
		purchasePrice,
		closingCostsComputed,
		totalClosingCosts,
		totalAcquisition,
		enclosedAddedCost,
		enclosedAddedValue,
		renoSubtotal,
		contingencyAmount,
		totalRenoCost,
		totalMonthlyHolding,
		totalHoldingCosts,
		arvWithEnclosed,
		adjustedARV,
		sellingCostsComputed,
		totalSellingCosts,
		totalCashInDeal,
		projectedProfit,
		projectedROI,
		maxAllowableOffer,
		passes70Rule,
		effectiveSellingPct,
		targetARV20,
		targetARVForIMO,
		profitMarginPct,
		ltcPct,
		ltvPct,
		isCashDeal,
	};
}

export function gradeDeal(roi: number): {label: string; color: string; tier: number} {
	if (roi >= 20) {
		return {label: 'STRONG', color: 'emerald', tier: 3};
	}
	if (roi >= 10) {
		return {label: 'WORKABLE', color: 'amber', tier: 2};
	}
	if (roi > 0) {
		return {label: 'THIN', color: 'orange', tier: 1};
	}
	return {label: 'PASS', color: 'red', tier: 0};
}

export function gradeProfitMargin(pct: number): {color: string; tier: number} {
	if (pct >= 20) {
		return {color: 'emerald', tier: 2};
	}
	if (pct >= 15) {
		return {color: 'amber', tier: 1};
	}
	return {color: 'red', tier: 0};
}

export function gradeLTC(pct: number): {color: string; tier: number} {
	if (pct <= 75) {
		return {color: 'emerald', tier: 2};
	}
	if (pct <= 80) {
		return {color: 'amber', tier: 1};
	}
	return {color: 'red', tier: 0};
}

export function gradeLTV(pct: number): {color: string; tier: number} {
	if (pct <= 70) {
		return {color: 'emerald', tier: 2};
	}
	if (pct <= 75) {
		return {color: 'amber', tier: 1};
	}
	return {color: 'red', tier: 0};
}
